import {
  ApplicationConfig,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, UrlSerializer } from '@angular/router';

import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
} from 'keycloak-angular';
import { providePrimeNG } from 'primeng/config';
import { IxUiHeightService } from 'ix-ng-core';
import {
  IX_FAVORITE_STORAGE_STRATEGY,
  IxKeyValueLookupService,
  IxNgAppVersionService,
  IxNgLocalStorageService,
  IxUiHeightServiceImpl,
  IxUrlSerializer,
} from 'ix-ng-components';
import { IxNgCacheService, IxNgRoutingService } from 'ix-ng-routing';
import { IxHttpActivity, IxHttpActivityService } from 'ix-ng-http';
import { IxNgErrorHandlingService, IxNgToastService, IxNgValidationService } from 'ix-ng-forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
  API_BASE_URL,
  BREADCRUMB_HOME_URL,
  ComVizitkaService,
  ErrorHandling,
  FavoriteStorageStrategy,
  httpBlockInterceptor,
  LocalStorage,
  NabiralnikService,
  PriljubljeniPoglediLookupService,
  ValidationService,
} from 'arsktrp-shared-components';
import { evtTheme } from 'shared-styles';
import { AppService, NabiralnikEvtService } from 'shared-components';
import { DOCUMENT } from '@angular/common';

const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: /^.*$/i,
  bearerPrefix: 'Bearer',
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideKeycloak({
      config: environment.keycloakConfig,
      initOptions: {
        // EVT je portal z javnim delom: `check-sso` preveri obstoječo sejo, a
        // anonimnega uporabnika NE preusmeri na prijavo — to se izvede ob neposrednem vstopu v
        // prijavljeni del (glej `authGuard`) ali ob kliku na gumb za prijavo.
        onLoad: 'check-sso',
        checkLoginIframe: false,
      },
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      ripple: true,
      inputStyle: 'filled',
      theme: {
        preset: evtTheme,
        options: { darkModeSelector: '.app-dark' },
      },
    }),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [urlCondition],
    },
    provideHttpClient(
      withInterceptors([includeBearerTokenInterceptor, httpBlockInterceptor]),
      withInterceptorsFromDi(),
    ),
    {
      provide: IxUiHeightService,
      useClass: IxUiHeightServiceImpl,
    },
    IxNgRoutingService,
    { provide: IxHttpActivity, useClass: IxHttpActivityService },
    { provide: UrlSerializer, useClass: IxUrlSerializer },
    { provide: IxNgLocalStorageService, useClass: LocalStorage },
    { provide: IxNgValidationService, useClass: ValidationService },
    { provide: IxNgErrorHandlingService, useExisting: ErrorHandling },
    { provide: IxNgAppVersionService, useClass: AppService },
    IxNgToastService,
    IxHttpActivityService,
    MessageService,
    IxNgCacheService,
    IxKeyValueLookupService,
    {
      provide: LOCALE_ID,
      useValue: 'sl',
    },
    {
      provide: API_BASE_URL,
      useValue: '/api/evt',
    },
    {
      provide: IX_FAVORITE_STORAGE_STRATEGY,
      useClass: FavoriteStorageStrategy,
    },
    ComVizitkaService,
    ConfirmationService,
    DialogService,
    { provide: NabiralnikService, useClass: NabiralnikEvtService },
    {
      provide: BREADCRUMB_HOME_URL,
      useFactory: (doc: Document) => new URL(doc.baseURI).pathname,
      deps: [DOCUMENT],
    },
    PriljubljeniPoglediLookupService,
  ],
};
